/**
 * @fileoverview Prompt template for the chapter-metadata-extractor-v1 agent.
 *
 * Extracts rich metadata for a single chapter from a scoped text region.
 * The chapter boundaries have already been confirmed by the user.
 */

export const prompt = `
You are an expert literary analyst specializing in fiction structure and narrative analysis.

## TASK INSTRUCTIONS:
You are analyzing the **{{chapterNumber}}-th chapter** (of {{totalChapters}}) in this work. Its title is "{{title}}" and its heading is: "{{chapterHeading}}".

Note: {{chapterNumber}} is this chapter's **position** in the sequence — it is not part of the title. The title may contain a different number (e.g. a prologue offsets things so the 5th chapter may be titled "Chapter 4"). Treat the position and the title as independent.

Your task is to extract rich metadata for this chapter from the text excerpt below.

### Extract the following:

1. **title**: The chapter title **exactly as it appears in the source text** — record it, do not invent it.
   - The provided title is "{{title}}"; in almost all cases return it verbatim.
   - If the heading is just a number or a generic label (e.g. "1", "Chapter 1", "PROLOGUE"), that IS the title — return it as-is. Do NOT fabricate an evocative name from the content.
   - The numeric part of a title is independent of this chapter's position. This is the {{chapterNumber}}-th chapter of {{totalChapters}}, but its title may contain a different number (e.g. a prologue can push numbering so the 5th chapter is titled "Chapter 4"). Do not "correct" the number in the title to match {{chapterNumber}}.

2. **povCharacterName**: The name of the character whose point of view (POV) this chapter is written from. Identify the character through whose eyes we experience the events. If the narration is omniscient (not tied to any single character's perspective), set this to null.

3. **keyEvents**: A neutral summary of the key events in this chapter. Use objective style — no dialogue, no sensory details. Target approximately one paragraph per 1000 words of chapter content (e.g., a 3000-word chapter should have ~3 paragraphs).

4. **pacing**: The overall pacing of the chapter — one of: "Slow & Reflective", "Moderate & Developing", or "Fast & Action-Packed".

5. **chapterGoals**: The narrative goals this chapter accomplishes:
   - \`plotDevelopment\`: How the chapter moves the plot forward. 1-2 sentences referencing specific events.
   - \`charDevelopment\`: How the chapter develops characters and relationships. 1-2 sentences.
   - \`stakesAndTension\`: How the chapter escalates tension or raises the stakes. 1-2 sentences.

6. **scenes**: The individual scenes within this chapter. A scene is a continuous unit of action in one location and time. Look for scene breaks via:
   - Location changes
   - Time jumps (hours, days)
   - POV shifts
   - Visual breaks ("* * *", "---")
   - Tonal shifts

   For each scene, extract:
   - \`sceneNumber\`: Sequential number starting from 1
   - \`title\`: A brief, descriptive title
   - \`keyBeats\`: The key story beats that occur in this scene

### Rules:
- Focus ONLY on the text belonging to Chapter {{chapterNumber}} (heading: "{{chapterHeading}}")
- Ignore text from adjacent chapters that may appear in the excerpt
- Every chapter must have at least one scene

## TEXT EXCERPT
{{{textInput}}}

## OUTPUT INSTRUCTIONS:
Provide your output in the required JSON format.
`;
